import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs';
import config from '../../admin/config';
import { PasswordService } from '../../api/password.service';
import { ChangeService } from '../../common/core/change.service';
import { HookDirective } from '../../common/core/hook';
import { useState, useTriggerLoad } from '../../common/core/hooks';
import { emailIsValid } from '../../common/input/email';
import { SPLIT_CLIENT } from '../../common/split/split.guard';
import { StringParam } from '../../common/state/route';
import { RouteService, useQueryParam } from '../../common/state/route.service';
import { TreatmentService } from '../../common/treatment/treatment.service';
import { AccountIdService } from '../account/account-id.service';

@Component({
  host: { class: 'rv-grid xlarge outer' },
  selector: 'forgot-password',
  styleUrls: ['./forgot-password.component.scss'],
  templateUrl: './forgot-password.component.html',
  providers: [
    AccountIdService,
    RouteService,
    ChangeService,
    TreatmentService,
    { provide: SPLIT_CLIENT, useValue: {} },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent extends HookDirective {
  constructor(
    private readonly routeService: RouteService,
    private readonly passwordService: PasswordService,
    private readonly router: Router,
    readonly _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  get _() {
    const [envIsZelis] = useState<boolean>(!!config.envIsZelis);

    const [reasonQueryParam] = useQueryParam(
      this.routeService,
      new StringParam('reason'),
    );
    const [emailQueryParam] = useQueryParam(
      this.routeService,
      new StringParam('email'),
    );
    const [email, setEmail] = useState(emailQueryParam ?? '');
    const [emailHasIssue, setEmailHasIssue] = useState(false);
    const [clientSideError, setClientSideError] = useState('');
    const [serverSideError, setServerSideError] = useState('');

    function validateAndSubmit() {
      clearAllErrors();
      if (email.length === 0) {
        setEmailHasIssue(true);
        setClientSideError('Please enter email address.');
      } else if (!emailIsValid(email)) {
        setEmailHasIssue(true);
        setClientSideError(
          'This does not appear to be a valid email address. Please make sure it is correct and try again.',
        );
      } else {
        doSubmit();
      }
    }
    function clearAllErrors() {
      setEmailHasIssue(false);
      setClientSideError('');
      setServerSideError('');
    }

    const [submitLoad, doSubmit] = useTriggerLoad(() => {
      return this.passwordService.sendResetEmail(email).pipe(
        catchError((e: any) => {
          setServerSideError(
            'We’re having issues. Please cancel and try again, or contact support.',
          );
          throw e;
        }),
        tap(() => {
          this.router.navigate(['login', 'sent'], {
            queryParams: {
              email: email,
            },
            replaceUrl: true,
          });
        }),
      );
    });

    return {
      envIsZelis,
      email,
      setEmail,
      emailHasIssue,
      clientSideError,
      serverSideError,
      clearAllErrors,
      validateAndSubmit,
      submitLoad,
      reasonQueryParam,
    };
  }

  openIntercom() {
    Intercom('show');
  }
}
